import { motion } from 'framer-motion';
import {
    ArrowLeft,
    BadgeCheck,
    Ban,
    Calendar,
    DollarSign,
    Mail,
    Printer
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';

const containerVariants={hidden:{opacity:0},visible:{opacity:1,transition:{when:'beforeChildren',staggerChildren:0.1}}};
const itemVariants={hidden:{opacity:0,y:20},visible:{opacity:1,y:0,transition:{type:'spring',stiffness:300,damping:24}}};

const InvoiceDetail=()=>{
  const {id}=useParams();
  const navigate=useNavigate();
  const [invoice,setInvoice]=useState(null);
  const [loading,setLoading]=useState(true);

  const fetchData=async()=>{
    try{
      setLoading(true);
      const res=await api.get(`/invoices/${id}`);
      setInvoice(res.data?.data||res.data);
    }catch(err){
      console.error('Fetch invoice failed',err);
      toast.error(err.response?.data?.message||'Unable to load invoice');
      navigate('/invoices');
    }finally{setLoading(false);}
  };
  useEffect(()=>{fetchData();},[id]);

  const handleReview=async(action)=>{
    try{
      let body={action};
      if(action==='reject'){
        const reason=window.prompt('Enter rejection reason');
        if(!reason) return;
        body.rejectedReason=reason;
      }
      await api.put(`/invoices/${id}/review`,body);
      toast.success(`Invoice ${action==='approve'?'approved':'rejected'}`);
      fetchData();
    }catch(err){
      console.error('Review failed',err);
      toast.error(err.response?.data?.message||'Action failed');
    }
  };

  const formatDate=(d)=>new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});
  const formatMoney=(amt,cur)=>`${cur} ${amt?.toLocaleString()}`;

  if(loading){return <div className="flex justify-center items-center h-64"><div className="loading-spinner w-8 h-8 border-4"/></div>;}
  if(!invoice){return null;}

  return(
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      <motion.div variants={itemVariants} className="flex items-center space-x-2">
        <button onClick={()=>navigate('/invoices')} className="p-2 rounded-lg hover:bg-gray-100"><ArrowLeft size={18}/></button>
        <h1 className="page-title">Invoice {invoice.invoiceNumber}</h1>
      </motion.div>

      {/* Invoice card */}
      <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-soft border border-gray-100 p-6">
        <div className="flex justify-between items-start flex-col lg:flex-row lg:items-center">
          <div>
            <p className="text-sm text-gray-500 mb-1">Employee</p>
            <h2 className="text-xl font-semibold text-gray-900">{invoice.employee?.firstName} {invoice.employee?.lastName}</h2>
            <div className="flex items-center text-sm text-gray-600"><Mail size={14} className="mr-1"/>{invoice.employee?.email}</div>
          </div>
          <div className="mt-4 lg:mt-0 space-y-1 text-sm">
            <div className="flex items-center"><Calendar size={14} className="mr-1 text-gray-500"/> Invoice Date: {formatDate(invoice.invoiceDate)}</div>
            <div className="flex items-center"><Calendar size={14} className="mr-1 text-gray-500"/> Due Date: {formatDate(invoice.dueDate)}</div>
            <div className="flex items-center"><DollarSign size={14} className="mr-1 text-gray-500"/> Amount: {formatMoney(invoice.totalAmount, invoice.currency||'USD')}</div>
            <div className="flex items-center">Status: <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${invoice.status==='Approved'?'bg-success-100 text-success-700':invoice.status==='Rejected'?'bg-secondary-100 text-secondary-700':invoice.status==='Submitted'?'bg-primary-100 text-primary-700':'bg-gray-100 text-gray-700'}`}>{invoice.status}</span></div>
          </div>
        </div>

        {/* Line items */}
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Line Items</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Hours</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Rate</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoice.lineItems?.map((li,i)=>(
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap text-sm">{formatDate(li.date)}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">{li.hours}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">{formatMoney(li.hourlyRate, invoice.currency||'USD')}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">{formatMoney(li.amount, invoice.currency||'USD')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Notes</h3>
            <p className="text-gray-700 whitespace-pre-line">{invoice.notes}</p>
          </div>
        )}

        {/* Action buttons */}
        {invoice.status==='Submitted' && (
          <div className="mt-8 flex space-x-3 justify-end">
            <button onClick={()=>handleReview('approve')} className="btn-success flex items-center"><BadgeCheck size={16} className="mr-1"/>Approve</button>
            <button onClick={()=>handleReview('reject')} className="btn-danger flex items-center"><Ban size={16} className="mr-1"/>Reject</button>
          </div>
        )}

        {/* Print */}
        <div className="mt-6 flex justify-end">
          <button onClick={()=>window.print()} className="btn-outline flex items-center"><Printer size={16} className="mr-1"/>Print</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default InvoiceDetail; 